import { useEffect, useRef, useState } from 'react'
import { useConsent } from './ConsentProvider'
import '../css/CookiePopUp.css'

const CookiePopUp = () => {
    const {
        consent,
        isDialogOpen,
        saveConsent,
        closeSettings,
    } = useConsent()
    const [showDetails, setShowDetails] = useState(false)
    const [spotify, setSpotify] = useState(false)
    const [adobeFonts, setAdobeFonts] = useState(false)
    const dialogRef = useRef(null)
    const headingRef = useRef(null)

    useEffect(() => {
        if (!isDialogOpen) return undefined

        setShowDetails(Boolean(consent))
        setSpotify(Boolean(consent?.spotify))
        setAdobeFonts(Boolean(consent?.adobeFonts))

        const previouslyFocused = document.activeElement
        const focusTimer = window.setTimeout(() => headingRef.current?.focus(), 0)

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && consent) {
                closeSettings()
                return
            }

            if (event.key !== 'Tab') return

            const focusableElements = dialogRef.current?.querySelectorAll(
                'button:not([disabled]), a[href], input:not([disabled])'
            )

            if (!focusableElements?.length) return

            const firstElement = focusableElements[0]
            const lastElement = focusableElements[focusableElements.length - 1]

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault()
                lastElement.focus()
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault()
                firstElement.focus()
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            window.clearTimeout(focusTimer)
            document.removeEventListener('keydown', handleKeyDown)
            previouslyFocused?.focus?.()
        }
    }, [closeSettings, consent, isDialogOpen])

    if (!isDialogOpen) return null

    const acceptAll = () => saveConsent({ spotify: true, adobeFonts: true })
    const rejectAll = () => saveConsent({ spotify: false, adobeFonts: false })
    const saveSelection = () => saveConsent({ spotify, adobeFonts })
    const openPrivacyPolicy = () => {
        document.querySelector('[data-modal="privacy"]')?.click()
    }

    return (
        <div className="cookie-consent-backdrop">
            <section
                aria-describedby="cookie-consent-description"
                aria-labelledby="cookie-consent-title"
                aria-modal="true"
                className="cookie-consent"
                ref={dialogRef}
                role="dialog"
            >
                {consent && (
                    <button
                        aria-label="Cookie-Einstellungen schließen"
                        className="cookie-consent__close"
                        onClick={closeSettings}
                        type="button"
                    >
                        ×
                    </button>
                )}

                <p className="cookie-consent__eyebrow">Datenschutz</p>
                <h2 id="cookie-consent-title" ref={headingRef} tabIndex="-1">
                    Cookie-Einstellungen
                </h2>

                {!showDetails ? (
                    <>
                        <p id="cookie-consent-description">
                            Diese Website verwendet notwendige Cookies. Optionale
                            externe Dienste laden wir nur mit Ihrer Zustimmung.
                        </p>

                        <div className="cookie-consent__primary-actions">
                            <button
                                className="cookie-consent__button cookie-consent__button--accept"
                                onClick={acceptAll}
                                type="button"
                            >
                                Alle erlauben
                            </button>
                            <button
                                className="cookie-consent__button cookie-consent__button--reject"
                                onClick={rejectAll}
                                type="button"
                            >
                                Nur notwendige
                            </button>
                        </div>

                        <div className="cookie-consent__text-actions">
                            <button onClick={() => setShowDetails(true)} type="button">
                                Einstellungen
                            </button>
                            <button onClick={openPrivacyPolicy} type="button">
                                Datenschutzerklärung
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p id="cookie-consent-description">
                            Wählen Sie selbst, welche externen Dienste geladen
                            werden dürfen. Nicht notwendige Dienste sind
                            standardmäßig deaktiviert.
                        </p>

                        <div className="cookie-consent__categories">
                            <div className="cookie-consent__category">
                                <div>
                                    <h3>Notwendige Speicherung</h3>
                                    <p>
                                        Speichert ausschließlich Ihre Auswahl in
                                        diesem Browser (maximal 180 Tage).
                                    </p>
                                </div>
                                <span className="cookie-consent__always-active">
                                    Immer aktiv
                                </span>
                            </div>

                            <label className="cookie-consent__category">
                                <span>
                                    <strong>Spotify-Player</strong>
                                    <small>
                                        Lädt den eingebetteten Musik-Player von
                                        Spotify AB. Dabei können IP-Adresse,
                                        Geräte- und Nutzungsdaten übermittelt und
                                        Cookies gesetzt werden.
                                    </small>
                                </span>
                                <span className="cookie-consent__switch">
                                    <input
                                        aria-label="Spotify-Player erlauben"
                                        checked={spotify}
                                        onChange={(event) =>
                                            setSpotify(event.target.checked)
                                        }
                                        type="checkbox"
                                    />
                                    <span aria-hidden="true" />
                                </span>
                            </label>

                            <label className="cookie-consent__category">
                                <span>
                                    <strong>Adobe Fonts</strong>
                                    <small>
                                        Lädt die Webschrift von Adobe. Adobe erhält
                                        dabei unter anderem die IP-Adresse und den
                                        Hostnamen dieser Website.
                                    </small>
                                </span>
                                <span className="cookie-consent__switch">
                                    <input
                                        aria-label="Adobe Fonts erlauben"
                                        checked={adobeFonts}
                                        onChange={(event) =>
                                            setAdobeFonts(event.target.checked)
                                        }
                                        type="checkbox"
                                    />
                                    <span aria-hidden="true" />
                                </span>
                            </label>
                        </div>

                        <p className="cookie-consent__legal-note">
                            Rechtsgrundlage ist Ihre Einwilligung nach Art. 6 Abs.
                            1 lit. a DSGVO und § 25 Abs. 1 TDDDG. Sie können Ihre
                            Auswahl jederzeit über „Datenschutzeinstellungen“ im
                            Footer ändern.
                        </p>

                        <div className="cookie-consent__primary-actions cookie-consent__primary-actions--settings">
                            <button
                                className="cookie-consent__button cookie-consent__button--accept"
                                onClick={saveSelection}
                                type="button"
                            >
                                Auswahl speichern
                            </button>
                            <button
                                className="cookie-consent__button cookie-consent__button--reject"
                                onClick={rejectAll}
                                type="button"
                            >
                                Nur notwendige
                            </button>
                        </div>

                        <div className="cookie-consent__text-actions">
                            <button onClick={acceptAll} type="button">
                                Alle erlauben
                            </button>
                            <button onClick={openPrivacyPolicy} type="button">
                                Datenschutzerklärung
                            </button>
                        </div>
                    </>
                )}
            </section>
        </div>
    )
}

export default CookiePopUp
